import {
  type PersonSportRankingResponse,
  PersonStatusTier,
  type RelatedRankingResponse,
} from '@on3/api';
import { On3IconDownArrow } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { StarRating } from '@on3/ui-lib/components/Player/StarRating';
import { StarRanking } from '@on3/ui-lib/components/Player/StarRating/Ranking';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { isValidJerseyNumber } from '@on3/ui-lib/utils/checks';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { createUrl } from '@on3/ui-lib/utils/url';
import { PlayerRankingItemStatus } from '@public/components/Db/Rankings/PlayerRankings/PlayerRankingsStatus';
import { VerifiedBadge } from '@public/components/Elite/VerifiedBadge/VerifiedBadge';
import { Icon } from '@public/components/Icon/Icon';
import { getAbbreviatedRank } from '@public/components/Nil/helpers';
import { AllowedRankingType } from '@public/pages/rivals/rankings/[rankingType]/[sport]/[year]';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './PlayerRankingsItem.module.scss';

export const PlayerRankingsItem = ({
  player,
  relatedModel,
}: {
  player?: PersonSportRankingResponse;
  relatedModel?: RelatedRankingResponse;
}) => {
  const {
    person,
    overallRank,
    jerseyNumber,
    highSchoolRating,
    consensusOverallRank,
    recruitmentKey,
    collegeTeam,
    ratings,
    nilValue,
  } = player || {};

  const {
    rating,
    status,
    predictions,
    classRank,
    name,
    age,
    slug,
    highSchool,
    classYear,
    homeTownName,
    defaultAsset,
    earlySignee,
    earlyEnrollee,
    athleteVerified,
    formattedHeight,
    weight,
  } = person || {};

  const { consensusPositionRank, consensusStateRank, positionRank, stateRank } =
    rating || {};

  const router = useRouter();
  const {
    rankingType = 'college',
    state,
    position,
    sport,
    year,
  } = router.query as {
    rankingType?: string;
    state?: string;
    position?: string;
    sport?: string;
    year?: string;
  };

  const isIndustry = rankingType === AllowedRankingType.IndustryPlayer;
  const isCollege = rankingType === 'college';
  const recruitHref = createUrl(['rivals', slug, 'industry-comparison']);

  const { rankingSports } = useSite();

  const consensusOverallRankDisplay =
    (position && consensusPositionRank) ||
    (state && consensusStateRank) ||
    (!state && !position && consensusOverallRank) ||
    '-';
  const overallRankDisplay =
    (position && positionRank) ||
    (state && stateRank) ||
    (!position && !state && overallRank) ||
    '-';

  const isIndustryRankable = rankingSports?.find(
    (sportData) => sportData.key === relatedModel?.ranking?.sportKey,
  )?.isIndustryRankable;

  const playerRating = useMemo(() => {
    if (isIndustry && isIndustryRankable) {
      return ratings?.find((r) => r.type === 'Industry');
    }

    return ratings?.find((r) => r.type === 'On3');
  }, [isIndustry, isIndustryRankable, ratings]);

  return (
    <div className={styles.block} data-ranking-type={rankingType} role="row">
      <dl
        aria-labelledby="rank"
        className={clsx(styles.rankContainer, {
          [styles.isConsensus]: isIndustry,
        })}
        role="cell"
      >
        <dt>Rank</dt>
        <dd data-active={!isIndustry}>{overallRankDisplay}</dd>
        {isIndustryRankable && !isCollege && (
          <>
            <dt>Industry Ranking</dt>
            <dd data-active={isIndustry}>{consensusOverallRankDisplay}</dd>
          </>
        )}
      </dl>
      <figure className={styles.avatar} role="cell">
        <Avatar
          altText={`${name} Avatar` || ''}
          domain={defaultAsset?.domain}
          fit="cover"
          height={100}
          source={defaultAsset?.source}
          type={defaultAsset?.type}
          width={100}
        />
      </figure>
      {isCollege && (
        <>
          {age && (
            <dl aria-labelledby="age" className={styles.age} role="cell">
              <dt>Age</dt>
              <dd>{age}</dd>
            </dl>
          )}
          {classRank && collegeTeam && (
            <figure className={styles.classRank} role="cell">
              <Avatar
                altText={`${collegeTeam?.fullName} logo`}
                domain={collegeTeam?.asset?.domain}
                height={30}
                source={collegeTeam?.asset?.source}
                teamAvatar
                title={collegeTeam?.fullName}
                type={collegeTeam?.asset?.type}
                width={30}
              />
              <figcaption>{getAbbreviatedRank(classRank)}</figcaption>
            </figure>
          )}
        </>
      )}
      <div className={styles.details} role="cell">
        <Link
          aria-labelledby="player"
          className={styles.name}
          href={createUrl(['rivals', slug])}
          useSiteColor
        >
          {name}
        </Link>

        <dl className={styles.vitalsContainer}>
          {!!classYear && (
            <>
              <dt>High School Class Year</dt>
              <dd>H.S. {classYear}</dd>
            </>
          )}
          {formattedHeight && (
            <>
              <dt>Height</dt>
              <dd>{formattedHeight}</dd>
            </>
          )}
          {!!weight && (
            <>
              <dt>Weight</dt>
              <dd>{weight}</dd>
            </>
          )}
          {isValidJerseyNumber(jerseyNumber) && isCollege && (
            <>
              <dt>Jersey Number</dt>
              <dd>#{jerseyNumber}</dd>
            </>
          )}
        </dl>
        <dl className={styles.homeContainer}>
          {highSchool?.name && (
            <>
              <dt>High School</dt>
              <dd>
                <Link href={createUrl(['high-school', highSchool?.urlSlug])}>
                  {highSchool?.name}
                </Link>
              </dd>
            </>
          )}
          {homeTownName && (
            <>
              <dt>Hometown</dt>
              <dd>({homeTownName})</dd>
            </>
          )}
        </dl>
        {(earlyEnrollee || earlySignee) && (
          <div className={styles.playerIcons}>
            {earlyEnrollee && (
              <Icon className={styles.statusIcon} iconName="EarlyEnrollee" />
            )}
            {earlySignee && (
              <Icon className={styles.statusIcon} iconName="EarlySignee" />
            )}
          </div>
        )}
      </div>
      <div aria-labelledby="rating" className={styles.stars} role="cell">
        <div className={styles.rosterStars}>
          <StarRating
            fiveStarPlus={playerRating?.fiveStarPlus}
            href={recruitHref}
            rating={playerRating?.rating}
            sport={sport}
            stars={playerRating?.stars}
            year={year}
          />
          {isCollege && (
            <figure className={styles.collegeTeamLogo}>
              <Avatar
                altText={`${collegeTeam?.fullName} logo`}
                domain={collegeTeam?.asset?.domain}
                height={20}
                source={collegeTeam?.asset?.source}
                teamAvatar
                title={collegeTeam?.fullName}
                type={collegeTeam?.asset?.type}
                width={20}
              />
            </figure>
          )}
        </div>

        {isCollege && (
          <StarRating
            className={styles.secondaryStars}
            fiveStarPlus={highSchoolRating?.fiveStarPlus}
            href={recruitHref}
            rating={highSchoolRating?.consensusRating}
            sport={sport}
            stars={highSchoolRating?.stars}
            year={classYear}
          />
        )}

        {!isCollege && (
          <StarRanking
            isConsensus={isIndustry}
            nationalRank={playerRating?.overallRank}
            positionAbbr={playerRating?.positionAbbr}
            positionRank={playerRating?.positionRank}
            sport={String(sport)}
            stateAbbr={playerRating?.stateAbbr}
            stateRank={playerRating?.stateRank}
            year={classYear}
          />
        )}
      </div>

      <dl aria-labelledby="nil" className={styles.nilContainer} role="cell">
        <dt className={styles.nilLabel}>NIL Value:</dt>
        <dd className={styles.nilValuation}>
          {!!nilValue && nilValue >= 5000 ? formatCurrency(nilValue) : '-'}
        </dd>
      </dl>
      <div
        aria-labelledby="athlete-verified"
        className={styles.verified}
        role="cell"
      >
        {athleteVerified ? (
          <VerifiedBadge tier={PersonStatusTier.Elite} />
        ) : (classYear || 0) > classYearClaimCutoff ? (
          <Link
            forceReload
            href={createUrl(['rivals', slug, 'claim-profile'])}
            useSiteColor
          >
            Claim Profile <On3IconDownArrow className={styles.arrow} />
          </Link>
        ) : null}
      </div>
      <div
        aria-labelledby="position"
        className={styles.positionAbbr}
        role="cell"
      >
        {player?.positionAbbreviation}
      </div>

      {!isCollege && (
        <div aria-labelledby="status" className={styles.status} role="cell">
          <PlayerRankingItemStatus
            predictions={predictions}
            recruitmentKey={recruitmentKey}
            slug={slug}
            sport={sport}
            status={status}
            year={`${year}`}
          />
        </div>
      )}
    </div>
  );
};
